import { useState, useEffect } from 'react';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import data from '../../../data/fabrica-software.json';

// JSON-LD Structured Data for Fábrica de Software
const jsonLdData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Fábrica de Software",
  "alternateName": "Desenvolvimento Sob Medida",
  "url": "https://www.targetsoftware.com.br/fabrica-software",
  "provider": {
    "@type": "Organization",
    "name": "Target Software",
    "url": "https://www.targetsoftware.com.br",
    "logo": "https://www.targetsoftware.com.br/assets/img/logo.png"
  },
  "description": "A Fábrica de Software da Target Software entrega soluções customizadas com foco em negócio, alta performance, rapidez de implantação, tecnologia de ponta e equipes multidisciplinares orientadas a resultados.",
  "serviceType": "Desenvolvimento de Software Sob Medida",
  "areaServed": {
    "@type": "Country",
    "name": "Brasil"
  },
  "audience": {
    "@type": "BusinessAudience",
    "businessSize": "SMB/Enterprise",
    "industry": "Financeiro, Varejo, Indústria, Educação, Tecnologia, Saúde"
  },
  "offers": {
    "@type": "Offer",
    "price": "Sob consulta",
    "priceCurrency": "BRL",
    "availability": "https://schema.org/InStock"
  },
  "brand": {
    "@type": "Brand",
    "name": "Target Software"
  },
  "hasPart": [
    {
      "@type": "Service",
      "name": "Mergulho no Negócio",
      "description": "Especialistas atuam junto ao cliente desde o início, garantindo alinhamento profundo com o negócio."
    },
    {
      "@type": "Service",
      "name": "Orientado a Resultado",
      "description": "Entregas planejadas de acordo com objetivos reais do cliente, sempre focadas em resultado."
    },
    {
      "@type": "Service",
      "name": "Know-how de Negócio",
      "description": "Gestores experientes atuam diretamente com as áreas de negócio, sem linguagem técnica."
    },
    {
      "@type": "Service",
      "name": "Know-how Técnico",
      "description": "Time atualizado com as melhores práticas e tecnologias do mercado."
    },
    {
      "@type": "Service",
      "name": "Tecnologia de Ponta",
      "description": "Uso de ferramentas modernas, garantindo alta performance e segurança."
    },
    {
      "@type": "Service",
      "name": "Foco em Agilidade",
      "description": "Relacionamento próximo com o cliente para entregas rápidas e alinhadas às expectativas."
    },
    {
      "@type": "Service",
      "name": "Velocidade de Implantação",
      "description": "Processos enxutos para acelerar o time to market das soluções desenvolvidas."
    },
    {
      "@type": "Service",
      "name": "Fórmula de Sucesso",
      "description": "Combinação de gestão, planejamento, mitigação de riscos e equipe multidisciplinar."
    }
  ],
  "keywords": [
    "fábrica de software",
    "desenvolvimento sob medida",
    "software personalizado",
    "desenvolvimento web",
    "aplicações corporativas",
    "squad de desenvolvimento",
    "sistemas empresariais",
    "tecnologia de ponta",
    "outsourcing de desenvolvimento",
    "soluções customizadas"
  ],
  "image": "https://www.targetsoftware.com.br/assets/img/fabrica-software-banner.png"
};

const FabricaSoftwarePage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { diferenciais, cases } = data;

  const handleContactClick = () => {
    window.location.href = '/contato';
  };

  // Add JSON-LD structured data to head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#2C3D48] via-[#3C4F5C] to-[#AEBCC6]">
        <div className="absolute inset-0">
          <img 
            src="/images/produtos/fabrica-software.jpg"
            alt="Fábrica de Software"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C3D48]/80 to-[#2C3D48]/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <i className="ri-code-s-slash-fill mr-2"></i>
              Soluções Sob Medida
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
              Fábrica de Software
            </h1>
            <p className="text-xl lg:text-2xl text-white max-w-4xl mx-auto mb-8 leading-relaxed">
              Desenvolvimento sob medida multiplataforma <br/> 
              <strong className="text-white"> com foco em agilidade</strong> e 
              <strong className="text-white"> entendimento de negócio</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Gestão orientada a resultado</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Gestores com know-how de negócios</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Entregas ágeis com alto valor agregado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Saiba Mais - Vídeo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Conheça a Fábrica de Software em Detalhes
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Descubra como nossa fábrica de software desenvolve soluções sob medida com foco 
            em agilidade, entendimento de negócio e resultados excepcionais.
          </p>
          <button
            onClick={() => setShowVideoModal(true)}
            className="inline-flex items-center px-8 py-4 bg-[#2C3D48] text-white font-semibold rounded-xl hover:bg-[#1F2A32] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line text-2xl mr-3"></i>
            Assistir Vídeo de Apresentação
          </button>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Diferenciais da Nossa Fábrica
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conheça os diferenciais que fazem da nossa Fábrica de Software a escolha ideal
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {diferenciais.map((diferencial, index) => (
              <div key={index} className="group">
                <div className="bg-gray-50 rounded-2xl p-6 h-full hover:bg-[#2C3D48] transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer">
                  <div className="w-12 h-12 bg-[#2C3D48] rounded-xl flex items-center justify-center mb-4 group-hover:bg-white transition-colors duration-300">
                    <i className={`${diferencial.icon} text-2xl text-white group-hover:text-[#2C3D48]`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-white transition-colors duration-300">
                    {diferencial.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    {diferencial.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases de Sucesso */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Cases de Sucesso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Projetos desenvolvidos com excelência para grandes empresas
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {cases.map((caso, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-6">
                  <img 
                    src={caso.logo} 
                    alt={caso.cliente}
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {caso.cliente}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {caso.descricao}
                </p>
                <ul className="mt-4 space-y-2">
                  {caso.indicadores && caso.indicadores.map((indicador, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 text-sm font-medium">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {indicador}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#2C3D48]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Pronto para desenvolver sua solução?
          </h2>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            Entre em contato conosco e descubra como nossa fábrica de software <br/> 
            pode criar a solução perfeita para o seu negócio.
          </p>
          <button
            onClick={handleContactClick}
            className="inline-flex items-center px-8 py-4 bg-white text-[#2C3D48] font-semibold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line text-xl mr-3"></i>
            Falar com Especialista
          </button>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Vídeo de Apresentação</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-gray-100 rounded-xl p-8 text-center">
              <i className="ri-video-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">
                O vídeo de apresentação será disponibilizado em breve. 
                Entre em contato para mais informações.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default FabricaSoftwarePage;